import type { FC, ReactNode } from "react";
import React, { useEffect } from "react";

import { useNavigation } from "@remix-run/react";
import clsx from "clsx";

import { SidebarNav } from "~/components/sidebar-nav";
import { GlobalLoading, useLoader } from "~/context/LoaderContext";
import { useNearTop } from "~/hooks/use-near-top";
import { useScreenSize } from "~/hooks/use-screen-size";
import { cn } from "~/lib/utils";

import { H1 } from "./ui/typography";

export interface LayoutProps {
    children?: ReactNode;
    hideBack?: boolean;
    showLogout?: boolean;
    action?: React.ReactNode;
    backNavOverride?: string;
    loading?: boolean;
    title?: React.ReactNode;
    description?: React.ReactNode;
    searchBar?: React.ReactNode;

    /* TODO: these are anti-pattern props and should be reworked */
    displayHeader?: ReactNode;
    offsetTop?: boolean;
    hideLogo?: boolean;
    narrow?: boolean;
    padTop?: boolean;
    fullWidth?: boolean;
    hideBackgroundImage?: boolean;
    logoOnly?: boolean;
    actions?: React.ReactNode;
    stickyHeader?: boolean;
    headerImageUrl?: string;
    hideSidebar?: boolean;
}

export const DesktopLayout: FC<LayoutProps> = ({
    children,
    backNavOverride,
    title,
    fullWidth = false,
    hideBackgroundImage = false,
    actions,
    searchBar,
    loading = false,
    stickyHeader = true,
    headerImageUrl,
    hideSidebar = false,
}: LayoutProps) => {
    const navigation = useNavigation();
    const isNearTop = useNearTop();
    // const { setLoading } = useLoader();

    // // Effect to control the global loader based on prop and navigation state
    // useEffect(() => {
    //     const isLoading = navigation.state === "loading" || loading;
    //     // setLoading(isLoading);
    //     // Optional: Ensure loader is turned off when component unmounts
    //     // return () => setLoading(false);
    // }, [navigation.state, loading, setLoading]);

    return (
        <React.Fragment>
            {/* Direct image element with fixed position - only show if showBackgroundImage is true */}
            {!hideBackgroundImage && (
                <div
                    className="hidden xl:block"
                    style={{
                        position: "fixed",
                        bottom: 0,
                        right: 0,
                        width: "125%" /* 50% larger than original 50% */,
                        height: "125%" /* 50% larger than original 50% */,
                        backgroundImage: "url(/social-image.png)",
                        backgroundSize: "contain",
                        backgroundPosition: "bottom right",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.3,
                        pointerEvents: "none",
                        zIndex: 0,
                        display: "block",
                        // More intense gradient - transparent for 70% of the way, then quickly fade in
                        maskImage:
                            "linear-gradient(115deg, transparent 72%, rgba(0, 0, 0, 1) 100%)",
                        WebkitMaskImage:
                            "linear-gradient(115deg, transparent 72%, rgba(0, 0, 0, 1) 100%)",
                    }}
                    aria-hidden="true"
                ></div>
            )}
            <GlobalLoading />

            {/* Make the main container transparent or white based on background image visibility */}
            <div
                className="items-left flex min-h-screen"
                style={{ backgroundColor: "transparent" }}
            >
                {/* AIDEV-NOTE: ScrollRestoration removed - already in root.tsx */}
                {!hideSidebar && <SidebarNav className="" />}
                <div
                    className="items-left flex max-w-none flex-1 flex-col text-rose-950"
                    style={{ backgroundColor: "transparent" }}
                    id="layout-body"
                >
                    {headerImageUrl && (
                        <div className="h-[250px] w-full">
                            <img
                                src={headerImageUrl}
                                alt="Header background"
                                className="size-full rounded-md object-cover"
                            />
                        </div>
                    )}
                    <header
                        className={cn(
                            "flex items-center pb-4 border-b md:border-none lg:pt-8 lg:mt-[2.0rem] px-12 lg:px-20",
                            headerImageUrl && "lg:pt-4",
                            stickyHeader &&
                                "sticky top-0 z-30 transition-colors duration-200 data-[scrolled=true]:bg-white/80 backdrop-blur-sm",
                        )}
                        data-scrolled={!isNearTop}
                    >
                        {(title || searchBar || actions) && (
                            <div
                                className={clsx(
                                    fullWidth
                                        ? "w-full h-full mt-0 pt-0"
                                        : "w-full",
                                    "flex w-full items-center min-h-12 justify-start align-center gap-4",
                                )}
                            >
                                {title && (
                                    <H1
                                        className={cn(
                                            "mb-0 ",
                                            searchBar ? "shrink-0" : "grow",
                                        )}
                                    >
                                        {title}
                                    </H1>
                                )}
                                {searchBar && (
                                    <div className="max-w-md flex-1">
                                        {searchBar}
                                    </div>
                                )}
                                {actions && (
                                    <div className="shrink-0">{actions}</div>
                                )}
                            </div>
                        )}
                    </header>
                    <main
                        className={cn(
                            "flex-1 px-12 lg:px-20",
                            fullWidth
                                ? "w-full h-full mt-0 "
                                : "pb-6 md:pb-8 w-full",
                        )}
                        style={{
                            backgroundColor: !hideBackgroundImage
                                ? "transparent"
                                : "white",
                        }}
                    >
                        {/* Content container with max width except for single session page or full width pages */}
                        <div
                            className={cn(
                                "text-left ",
                                !fullWidth ? "max-w-[600px]" : "max-w-none",
                            )}
                        >
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </React.Fragment>
    );
};

export default DesktopLayout;
